import type { CompleteMicroSegmentationResult, MicroUnit } from '../types/segmentationTypes'

export interface UnitIdentitySpan {
  start: number
  end: number
  stableId: string
}

export interface UnitSpan {
  start: number
  end: number
}

const MIN_OVERLAP_CHARS = 2
const MIN_OVERLAP_RATIO = 0.25

const normalizeNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  return null
}

const spanLength = (span: UnitSpan): number => Math.max(0, span.end - span.start)

const overlapLength = (left: UnitSpan, right: UnitSpan): number => {
  const start = Math.max(left.start, right.start)
  const end = Math.min(left.end, right.end)
  return Math.max(0, end - start)
}

const normalizeIdRemap = (idRemap: unknown): Record<string, string[]> => {
  if (!idRemap || typeof idRemap !== 'object') {
    return {}
  }

  const normalized: Record<string, string[]> = {}
  Object.entries(idRemap as Record<string, unknown>).forEach(([source, targets]) => {
    const sourceKey = String(source || '').trim()
    if (!sourceKey || !Array.isArray(targets)) {
      return
    }

    const dedupedTargets = Array.from(new Set(
      targets
        .map(target => String(target || '').trim())
        .filter(target => target.length > 0)
    ))
    if (dedupedTargets.length > 0) {
      normalized[sourceKey] = dedupedTargets
    }
  })
  return normalized
}

export const extractIdentityUnitsFromSegmentationResult = (
  result: CompleteMicroSegmentationResult | null | undefined
): UnitIdentitySpan[] => {
  if (!result || !Array.isArray(result.chunk_results)) {
    return []
  }

  const units: UnitIdentitySpan[] = []
  result.chunk_results.forEach(chunk => {
    const microUnits: MicroUnit[] = Array.isArray(chunk?.micro_units) ? chunk.micro_units as MicroUnit[] : []
    microUnits.forEach(unit => {
      const start = normalizeNumber(unit?.start_position)
      const end = normalizeNumber(unit?.end_position)
      if (start === null || end === null || end <= start) {
        return
      }
      const stableIdRaw = (
        (typeof unit?.unit_uuid === 'string' && unit.unit_uuid.length > 0 && unit.unit_uuid) ||
        (typeof unit?.unit_id === 'string' && unit.unit_id.length > 0 && unit.unit_id)
      )
      if (!stableIdRaw) {
        return
      }
      units.push({
        start,
        end,
        stableId: stableIdRaw
      })
    })
  })

  return units.sort((left, right) => {
    if (left.start !== right.start) {
      return left.start - right.start
    }
    if (left.end !== right.end) {
      return left.end - right.end
    }
    return left.stableId.localeCompare(right.stableId)
  })
}

export const assignStableUnitIdsByOverlap = ({
  nextSpans,
  previousSpans,
  createId
}: {
  nextSpans: UnitSpan[]
  previousSpans: UnitIdentitySpan[]
  createId: (index: number) => string
}): string[] => {
  if (nextSpans.length === 0) {
    return []
  }
  if (previousSpans.length === 0) {
    return nextSpans.map((_, index) => createId(index))
  }

  const matchedIds = new Array<string | null>(nextSpans.length).fill(null)
  const usedPrevious = new Set<number>()
  const candidates: Array<{ nextIndex: number; previousIndex: number; overlap: number; overlapRatio: number }> = []

  nextSpans.forEach((nextSpan, nextIndex) => {
    previousSpans.forEach((previousSpan, previousIndex) => {
      const overlap = overlapLength(nextSpan, previousSpan)
      if (overlap < MIN_OVERLAP_CHARS) {
        return
      }
      const maxLen = Math.max(spanLength(nextSpan), spanLength(previousSpan))
      // mutation-ignore-next-line equivalent: zero-length spans cannot reach this branch with a positive overlap.
      if (maxLen <= 0) {
        return
      }
      const overlapRatio = overlap / maxLen
      if (overlapRatio < MIN_OVERLAP_RATIO) {
        return
      }
      if (overlap > 0) {
        candidates.push({
          nextIndex,
          previousIndex,
          overlap,
          overlapRatio
        })
      }
    })
  })

  candidates
    .sort((left, right) => {
      if (right.overlapRatio !== left.overlapRatio) {
        return right.overlapRatio - left.overlapRatio
      }
      if (right.overlap !== left.overlap) {
        return right.overlap - left.overlap
      }
      // mutation-ignore-next-line equivalent: candidates are generated in next-index order before this stable tie-break.
      if (left.nextIndex !== right.nextIndex) {
        return left.nextIndex - right.nextIndex
      }
      return left.previousIndex - right.previousIndex
    })
    .forEach(candidate => {
      if (matchedIds[candidate.nextIndex] || usedPrevious.has(candidate.previousIndex)) {
        return
      }
      matchedIds[candidate.nextIndex] = previousSpans[candidate.previousIndex].stableId
      usedPrevious.add(candidate.previousIndex)
    })

  return matchedIds.map((matchedId, index) => matchedId || createId(index))
}

export const buildUnitReferenceIndexWithRemap = ({
  units,
  idRemap
}: {
  units: MicroUnit[]
  idRemap?: Record<string, string[]> | null
}): Map<string, number> => {
  const lookup = new Map<string, number>()
  units.forEach((unit, unitIndex) => {
    const references = [unit.unit_uuid, unit.unit_id, unit.unit_label]
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .map(value => value.trim())
    references.forEach(reference => {
      if (!lookup.has(reference)) {
        lookup.set(reference, unitIndex)
      }
    })
  })

  const normalizedRemap = normalizeIdRemap(idRemap)
  const resolveRemappedIndex = (reference: string, seen: Set<string>): number | undefined => {
    if (lookup.has(reference)) {
      return lookup.get(reference)
    }
    if (seen.has(reference)) {
      return undefined
    }
    seen.add(reference)
    const remappedTargets = normalizedRemap[reference]
    // mutation-ignore-next-line equivalent: normalizeIdRemap only keeps non-empty target arrays.
    if (!Array.isArray(remappedTargets) || remappedTargets.length === 0) {
      return undefined
    }

    for (const candidate of remappedTargets) {
      const resolved = resolveRemappedIndex(candidate, seen)
      if (resolved !== undefined) {
        return resolved
      }
    }
    return undefined
  }

  Object.keys(normalizedRemap).forEach(sourceRef => {
    if (lookup.has(sourceRef)) {
      return
    }
    const resolvedIndex = resolveRemappedIndex(sourceRef, new Set<string>())
    if (resolvedIndex !== undefined) {
      lookup.set(sourceRef, resolvedIndex)
    }
  })

  return lookup
}
